"use client";

import React, { useEffect, useMemo, useState } from "react";
import { LogInIcon } from "lucide-react";
import { useDynamicContext, useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import { Button } from "@/components/ui/button";
import { useAuth, useBreakpoints, useTranslate } from "@/hooks";
import { useSidebar } from "@/contexts/SidebarContext";
import { useDispatch, useSelector } from "react-redux";
import { authActions, type RootState } from "@/store";
import AvatarPopup from "@/components/layout/AvatarPopup";
import { AvatarBadge } from "../main";
import { useProfile } from "@/api/userQueries";

const User = () => {
  const t = useTranslate();
  const { isAuthenticated, isInitialized } = useAuth();
  const { user, handleLogOut, setShowAuthFlow } = useDynamicContext();
  const isLoggedIn = useIsLoggedIn();
  const { isExpanded } = useSidebar();
  const { isGreaterThan } = useBreakpoints();
  const dispatch = useDispatch();
  const triggerLogin = useSelector(
    (state: RootState) => state.auth.triggerLogin
  );
  const { refetch: refetchProfile } = useProfile({ enabled: false });

  React.useEffect(() => {
    if (isAuthenticated && isInitialized) {
      refetchProfile();
    }
  }, [isAuthenticated, isInitialized, refetchProfile]);

  // Set CSS variable for sidebar width based on expanded state
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-expanded-width",
      isExpanded ? "240px" : "80px"
    );
  }, [isExpanded]);

  const resolvedUsername = useMemo(
    () => (isLoggedIn && user ? user.username : ""),
    [isLoggedIn, user]
  );

  useEffect(() => {
    if (triggerLogin) {
      const triggerAuth = async () => {
        await handleLogOut();
        setShowAuthFlow(true);
        dispatch(authActions.toggleTriggerLogin());
      };

      triggerAuth();
    }
  }, [triggerLogin, handleLogOut, setShowAuthFlow, dispatch]);

  const handleAuthButton = async () => {
    if (isLoggedIn) {
      await handleLogOut();
    } else {
      setShowAuthFlow(true);
    }
  };

  const shouldShowUserInfo =
    isExpanded || (!isExpanded && !isGreaterThan("lg"));

  return (
    <div className="flex flex-col items-center justify-center gap-2 pt-4 lg:flex-row xl:gap-4">
      {isLoggedIn ? (
        <>
          <AvatarPopup
            onSignOut={handleAuthButton}
            resolvedUsername={resolvedUsername || ""}
            shouldShowUserInfo={shouldShowUserInfo}
          />
        </>
      ) : isExpanded ? (
        <div className="flex w-full flex-row items-center gap-2">
          <div
            onClick={handleAuthButton}
            className="cursor-pointer transition duration-150 hover:scale-95">
            <AvatarBadge
              className="size-10 xl:size-12 2xl:size-16"
              ringClassName="border-slate-500"
            />
          </div>
          <div className="flex flex-col">
            <p className="mb-1 text-xs lg:text-base xl:text-lg">
              {resolvedUsername}
            </p>
            <Button
              variant="ghost"
              className="flex h-auto items-center gap-2 px-0 py-0 text-xs font-normal text-slate-400 hover:bg-transparent hover:text-slate-400 focus:text-slate-400 lg:text-sm"
              onClick={handleAuthButton}>
              {t("auth.sign_in")}
              <LogInIcon className="hidden !size-3.5 lg:flex lg:size-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[50px] w-full flex-col items-center">
          <button
            className="flex items-center transition duration-150 hover:scale-95"
            onClick={() => dispatch(authActions.toggleTriggerLogin())}>
            <div className="relative flex size-10 items-center justify-center">
              <div className="absolute inset-0 -m-1 rounded-full border border-dashed border-slate-500" />
              <div className="flex size-9 items-center justify-center rounded-full bg-slate-200 text-slate-900">
                <LogInIcon className="size-5" />
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(User);
